import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import './ui-polish.css';
import { Routes, Route, Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Register from './components/Register';
import Login from './components/Login';
import Landing from './components/Landing';
import ProtectedRoute from './components/ProtectedRoute';
import RecipeExtractor from './components/RecipeExtractor';
import SavedRecipes from './components/SavedRecipes';
import CookMode from './components/CookMode';

import Profile from './components/Profile';
import SavedRecipeDetail from './components/SavedRecipeDetail';
import OnboardingModal from './components/OnboardingModal';
import AdminAds from './components/AdminAds';
import AdminPanel from './components/AdminPanel';
import AdminUsers from './components/AdminUsers';
import AdminPayments from './components/AdminPayments';
import AdminLayout from './components/AdminLayout';
import AdminRoute from './components/AdminRoute';
import Unauthorized from './components/Unauthorized';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import { useUser } from './context/UserContext';

function App() {
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedMode = localStorage.getItem('darkMode');
        if (savedMode === null) return true;
        return savedMode === 'true';
    });
    const navigate = useNavigate();
    const location = useLocation();
    const { resetUser, user } = useUser();

    const handleLogout = useCallback(async () => {
        try {
            await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
        } catch (error) {}
        localStorage.removeItem('token');
        resetUser();
        navigate('/');
    }, [navigate, resetUser]);

    useEffect(() => {
        localStorage.setItem('darkMode', isDarkMode);
        if (isDarkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [isDarkMode]);

    useEffect(() => {
        setIsNavOpen(false);
    }, [location.pathname]);

    const handleLogin = (token) => {
        if (token) localStorage.setItem('token', token);
        navigate('/app');
    };

    const toggleDarkMode = () => {
        setIsDarkMode(prevMode => !prevMode);
    };

    const closeNav = () => setIsNavOpen(false);

    // Determine admin access in client (also enforced server-side). Use REACT_APP_ADMIN_EMAILS comma-separated.
    const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    const showAdmin = !!(user && (user.admin || adminEmails.includes((user.email || '').toLowerCase())));
    const isAuthenticated = !!user;

    return (
        <div className="app-shell">
            <a className="skip-link" href="#main">Skip to content</a>
            <header className="app-header">
                <nav className="navbar navbar-expand-lg app-navbar">
                    <div className="container-fluid">
                        <Link className="navbar-brand" to={isAuthenticated ? '/app' : '/'} onClick={closeNav}>
                            RecipeCore
                        </Link>
                        <button
                            className="navbar-toggler"
                            type="button"
                            onClick={() => setIsNavOpen((prev) => !prev)}
                            aria-controls="appNavBar"
                            aria-expanded={isNavOpen}
                            aria-label="Toggle navigation"
                        >
                            <span className="navbar-toggler-icon"></span>
                        </button>
                        <div className={`collapse navbar-collapse${isNavOpen ? ' show' : ''}`} id="appNavBar">
                            <ul className="navbar-nav ms-auto align-items-lg-center">
                                {isAuthenticated ? (
                                    <>
                                        {showAdmin && (
                                            <li className="nav-item">
                                                <NavLink
                                                    to="/admin"
                                                    onClick={closeNav}
                                                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                                >
                                                    Admin
                                                </NavLink>
                                            </li>
                                        )}

                                        <li className="nav-item">
                                            <NavLink
                                                to="/app"
                                                onClick={closeNav}
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Workspace
                                            </NavLink>
                                        </li>

                                        <li className="nav-item">
                                            <NavLink
                                                to="/saved-recipes"
                                                onClick={closeNav}
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Saved recipes
                                            </NavLink>
                                        </li>

                                        <li className="nav-item">
                                            <NavLink
                                                to="/profile"
                                                onClick={closeNav}
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Profile
                                            </NavLink>
                                        </li>

                                        {showAdmin && (
                                            <li className="nav-item">
                                                <NavLink
                                                    to="/admin/monetization"
                                                    onClick={closeNav}
                                                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                                >
                                                    Monetization
                                                </NavLink>
                                            </li>
                                        )}

                                        <li className="nav-item">
                                            <button
                                                className="btn btn-link nav-link"
                                                type="button"
                                                onClick={() => {
                                                    closeNav();
                                                    handleLogout();
                                                }}
                                            >
                                                Logout
                                            </button>
                                        </li>
                                    </>
                                ) : (
                                    <>
                                        <li className="nav-item">
                                            <NavLink
                                                to="/"
                                                end
                                                onClick={closeNav}
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Overview
                                            </NavLink>
                                        </li>
                                        <li className="nav-item">
                                            <NavLink
                                                to="/register"
                                                onClick={closeNav}
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Register
                                            </NavLink>
                                        </li>
                                        <li className="nav-item">
                                            <NavLink
                                                to="/login"
                                                onClick={closeNav}
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Login
                                            </NavLink>
                                        </li>
                                    </>
                                )}
                                <li className="nav-item ms-lg-3 mt-3 mt-lg-0">
                                    <button
                                        type="button"
                                        className={`theme-toggle ${isDarkMode ? 'is-dark' : ''}`}
                                        onClick={toggleDarkMode}
                                        aria-pressed={isDarkMode}
                                    >
                                        <span className="theme-toggle-track">
                                            <span className="theme-toggle-thumb"></span>
                                        </span>
                                        <span className="theme-toggle-label">
                                            {isDarkMode ? 'Dark' : 'Light'} mode
                                        </span>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </nav>
            </header>
            <OnboardingModal />

            <main id="main" className="app-main">
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/login" element={<Login onLogin={handleLogin} />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/app" element={<ProtectedRoute><RecipeExtractor /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} onLogout={handleLogout} /></ProtectedRoute>} />
                    <Route path="/saved-recipes" element={<ProtectedRoute><SavedRecipes /></ProtectedRoute>} />
                    <Route path="/recipes/:id" element={<ProtectedRoute><SavedRecipeDetail /></ProtectedRoute>} />
                    <Route path="/cook-mode" element={<ProtectedRoute><CookMode /></ProtectedRoute>} />
                    <Route path="/unauthorized" element={<ProtectedRoute><Unauthorized /></ProtectedRoute>} />
                    <Route path="/admin" element={<ProtectedRoute><AdminRoute><AdminLayout /></AdminRoute></ProtectedRoute>}>
                        <Route index element={<AdminPanel />} />
                        <Route path="monetization" element={<AdminAds />} />
                        <Route path="ads" element={<AdminAds />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="payments" element={<AdminPayments />} />
                    </Route>
                </Routes>
            </main>

            <footer className="app-footer">
                <div className="footer-card">
                    Crafted for cooks who want signal, not scroll.{' '}
                    <Link to="/privacy">Privacy Policy</Link> · <Link to="/terms">Terms of Service</Link>
                </div>
            </footer>
        </div>
    );
}

export default App;
